/**
 * PartsCounterRow Component
 *
 * Expandable row displaying a single parts counter person's performance.
 * Collapsed: Shows name, gross, "X txns / X lost".
 * Expanded: Shows pay type breakdown table (Sales, Cost, Gross, GP%).
 */

import React from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { PartsCounterPerson, PayTypeMetrics } from './types';

interface PartsCounterRowProps {
  person: PartsCounterPerson;
  isExpanded: boolean;
  onToggle: () => void;
  rank: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}%`;
}

const PAY_TYPES: Array<{
  key: keyof Pick<PartsCounterPerson, 'counterRetail' | 'counterWholesale' | 'customerParts' | 'internalParts' | 'warrantyParts' | 'other'>;
  label: string;
}> = [
  { key: 'counterRetail', label: 'Counter Retail' },
  { key: 'counterWholesale', label: 'Counter Wholesale' },
  { key: 'customerParts', label: 'Customer Parts' },
  { key: 'warrantyParts', label: 'Warranty Parts' },
  { key: 'internalParts', label: 'Internal Parts' },
  { key: 'other', label: 'Other' },
];

export const PartsCounterRow: React.FC<PartsCounterRowProps> = ({
  person,
  isExpanded,
  onToggle,
  rank,
}) => {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Main row - always visible */}
      <div
        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-gray-400 transition-transform duration-200">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <span className="text-xs text-gray-400 font-medium w-5">{rank}</span>
          <span className="font-medium text-gray-900 truncate">
            {person.employeeName}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <span className="text-lg font-bold text-green-600">
              {formatCurrency(person.grossTotal)}
            </span>
          </div>
          <div className="text-right text-sm w-28">
            <span className="text-gray-900">{person.transactionCount}</span>
            <span className="text-gray-400 text-xs ml-1">tickets</span>
            <span className="text-gray-400 mx-1">/</span>
            <span className={person.lostSalesCount > 0 ? 'text-red-600' : 'text-gray-400'}>{person.lostSalesCount}</span>
            <span className="text-gray-400 text-xs ml-1">lost</span>
          </div>
        </div>
      </div>

      {/* Expanded details - pay type breakdown */}
      {isExpanded && (
        <div className="ml-12 mr-3 mb-3 bg-gray-50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wider">
                <th className="text-left py-2 px-3 font-medium">Pay Type</th>
                <th className="text-right py-2 px-3 font-medium">Sales</th>
                <th className="text-right py-2 px-3 font-medium">Cost</th>
                <th className="text-right py-2 px-3 font-medium">Gross</th>
                <th className="text-right py-2 px-3 font-medium">GP%</th>
              </tr>
            </thead>
            <tbody>
              {PAY_TYPES.map((payType) => {
                const metrics = person[payType.key] as PayTypeMetrics;
                if (metrics.sales === 0 && metrics.cost === 0 && metrics.gross === 0) {
                  return null;
                }
                return (
                  <tr
                    key={payType.key}
                    className="border-t border-gray-200 hover:bg-white"
                  >
                    <td className="py-2 px-3 text-gray-700">{payType.label}</td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatCurrency(metrics.sales)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatCurrency(metrics.cost)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900 font-medium">
                      {formatCurrency(metrics.gross)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-900">
                      {formatPercent(metrics.gpPct)}
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr className="border-t-2 border-gray-300 bg-gray-100 font-medium">
                <td className="py-2 px-3 text-gray-700">Total</td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {formatCurrency(person.salesTotal)}
                </td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {formatCurrency(person.costTotal)}
                </td>
                <td className="py-2 px-3 text-right text-green-600 font-bold">
                  {formatCurrency(person.grossTotal)}
                </td>
                <td className="py-2 px-3 text-right text-gray-900">
                  {formatPercent(person.gpPctTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
